import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ZenGridLogo from '../common/ZenGridLogo';
import Block from './Block';
import Sidebar from './Sidebar';
import SettingsModal from './SettingsModal';
import { generateFullChartByAI } from '../api/ai';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useUI } from '../contexts/UIContext';

const MainView = ({ chartData, updateCell, updateCellPriority, bulkUpdateChart }) => {
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [isFullGenerating, setIsFullGenerating] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const settings = useSettings();
  const { toast } = useUI();

  const activeBlock = activeBlockId
    ? chartData.blocks.find(b => b.id === activeBlockId)
    : null;

  const handleFullAIGenerate = async () => {
    const coreBlock = chartData.blocks.find(b => b.block_type === 'CORE');
    const centerTheme = coreBlock?.cells.find(c => c.is_center)?.content;
    if (!centerTheme) {
      toast('コア・ブロックの中心にメインテーマを入力してください。', 'error');
      return;
    }
    setIsFullGenerating(true);
    try {
      const newChart = await generateFullChartByAI(chartData, settings);
      await bulkUpdateChart(newChart);
      toast(`「${centerTheme}」に基づく81マスの生成が完了しました！`, 'success');
    } catch (err) {
      console.error(err);
      toast(err.message || 'AIの生成中にエラーが発生しました', 'error');
      if (err.message.includes('APIキー')) setIsSettingsOpen(true);
    } finally {
      setIsFullGenerating(false);
    }
  };

  const handleSwapCells = (blockId, sourceCellId, targetCellId) => {
    const block = chartData.blocks.find(b => b.id === blockId);
    if (!block) return;
    const targetCell = block.cells.find(c => c.id === targetCellId);
    if (!targetCell || targetCell.is_center) return;
    updateCellPriority(blockId, sourceCellId, targetCell.priority_index);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-sky-100 dark:from-[#0a0a12] dark:via-[#0f0f1a] dark:to-[#111124] overflow-hidden font-sans transition-colors duration-300">
      <header className="h-16 glass-panel border-x-0 border-t-0 rounded-none px-4 flex items-center justify-between z-10 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/')} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            <SafeIcon icon={FiIcons.FiArrowLeft} className="text-xl" />
          </button>
          <h1 className="font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2 tracking-tight min-w-0">
            <ZenGridLogo className="w-6 h-6 shrink-0 rounded-lg shadow-sm" />
            <span className="shrink-0">ZenGrid</span>
            <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400 font-medium truncate">— AIアシスト搭載の9マス目標達成ツール</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!activeBlockId && (
            <button onClick={handleFullAIGenerate} disabled={isFullGenerating} className="px-3.5 py-2 text-sm font-semibold bg-primary-600 hover:bg-primary-500 text-white rounded-full shadow-md shadow-primary-600/25 flex items-center gap-1.5 transition-colors">
              <SafeIcon icon={isFullGenerating ? FiIcons.FiLoader : FiIcons.FiCpu} className={isFullGenerating ? "animate-spin" : ""} />
              <span className="hidden sm:inline">全自動AI生成</span>
            </button>
          )}
          {activeBlockId && (
            <button onClick={() => setActiveBlockId(null)} className="px-3.5 py-2 text-sm font-semibold glass-btn-secondary flex items-center gap-1.5">
              <SafeIcon icon={FiIcons.FiZoomOut} /> 全体俯瞰
            </button>
          )}
          <button onClick={() => setIsSettingsOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10 transition-colors ml-1">
            <SafeIcon icon={FiIcons.FiSettings} className="text-lg" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        <main className="flex-1 p-2 md:p-6 flex items-center justify-center relative overflow-hidden min-h-0">
          <AnimatePresence mode="wait">
            {!activeBlockId ? (
              <motion.div
                key="global-view"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.1 }}
                className="grid grid-cols-3 grid-rows-3 gap-1 md:gap-3 w-full h-full aspect-square mx-auto"
                style={{ maxWidth: 'min(100%, 80vh)', maxHeight: 'min(100%, 80vw)' }}
              >
                {chartData.blocks.map(block => (
                  <Block key={block.id} block={block} isZoomed={false} onCellUpdate={updateCell} onBlockClick={setActiveBlockId} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="zoomed-view"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="w-full h-full mx-auto flex items-center justify-center"
                style={{ maxWidth: 'min(100%, 75vh)', maxHeight: 'min(100%, 75vw)' }}
              >
                <Block block={activeBlock} isZoomed={true} onCellUpdate={updateCell} onSwapCells={handleSwapCells} />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <AnimatePresence>
          {activeBlockId && (
            <motion.div
              initial={{ x: 320 }}
              animate={{ x: 0 }}
              exit={{ x: 320 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 z-20 md:relative md:top-0 shadow-[-10px_0_30px_-5px_rgba(0,0,0,0.15)] w-80 shrink-0 backdrop-blur-2xl bg-white/70 dark:bg-white/[0.06] border-l border-white/70 dark:border-white/10"
            >
              <Sidebar activeBlock={activeBlock} updateCellPriority={updateCellPriority} updateCell={updateCell} chartData={chartData} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
};

export default MainView;